import type {ActionResponse} from 'src/typings';
import type {ModalDialogService} from 'shared/modal-dialogs';
import type {ToastMessageService} from 'shared/components/kp-toast-messages';
import type {ModalWindowTypeToModelMap} from 'shared/modal-dialogs/config';
import {isInterruptedActionResponse} from 'src/modals/modal-utils';
import {isActionResponse, isActionResponseWithText} from '../utils/types-utils';
import {resolveErrorMessage} from 'shared/utils/error-utils';
import {responseTypes} from '../constants';
import {assertIsModalWindowType} from 'shared/modal-dialogs/utils';

/**
 * @ngdoc service
 * @name finishedResponseInteractionService
 * @module portaro.services
 *
 * @required toastMessageService
 * @required modalDialogService
 *
 * @description
 * Service encapsulates logic of all options how to display finishedResponses to user
 * Service can display toasts or modal windows
 * there are 2 kinds of methods: for success responses called from then(), and for error responses, called from catch()
 */
export default class FinishedResponseInteractionService {
    public static readonly serviceName = 'finishedResponseInteractionService';
    private readonly ignoredResponseTypes = [responseTypes.INTERRUPT, responseTypes.ACKNOWLEDGE] as const;
    private readonly forceModalResponseTypes = [responseTypes.REDIRECTION, responseTypes.PRINT_PAGE, responseTypes.RICH_TEXT_FINISHED_RESPONSE] as const;

    /*@ngInject*/
    constructor(private toastMessageService: ToastMessageService, private modalDialogService: ModalDialogService) {}

    public async getResultOrFailedModalIgnoringManualInterruption<RESP>(callback: () => Promise<RESP>): Promise<RESP> {
        try {
            return await callback();
        } catch (actionResponse) {
            if (!isActionResponseWithText(actionResponse) || !isInterruptedActionResponse(actionResponse)) {
                await this.showFailedResponseInModalWindow(actionResponse);
            }
            throw actionResponse;
        }
    }

    public async showSuccessToastOrFailedModalIgnoringManualInterruption<RESP extends ActionResponse>(callback: () => Promise<RESP>): Promise<RESP> {
        return this.getResultOrFailedModalIgnoringManualInterruption(async () => {
            const response = await callback();
            await this.showSuccessResponseInToastIfPossible(response);
            return response;
        });
    }

    public async showSuccessResponseInToastIfPossible(actionResponse: ActionResponse): Promise<void> {
        if (this.isForceModal(actionResponse.responseType)) {
            await this.showSuccessResponseInModalWindow(actionResponse);
            return;
        }
        if (isActionResponseWithText(actionResponse) && !this.isIgnored(actionResponse.responseType)) {
            this.toastMessageService.showSuccess(actionResponse.text);
        }
        return Promise.resolve();
    }

    public showSuccessResponseInModalWindow(result: ActionResponse) {
        return this.showModalWindow(result, false);
    }

    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    public showFailedResponseInToast(result: ActionResponse | any) {
        if (isActionResponse(result)) {
            return this.showErrorToast(result);
        }
        return this.showNonStandardError(result);
    }

    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    public showFailedResponseInModalWindow(result: ActionResponse | any) {
        if (isActionResponse(result)) {
            return this.showModalWindow(result, true);
        }
        this.showNonStandardError(result);
        throw result;
    }

    /**
     * @ngdoc method
     * @name finishedResponseInteractionService#showFailedInModalWindow
     *
     * @param {any} result Some type of response, without explicit type
     **
     * @description
     * Takes response and displays modal window  if it is accepted type of response
     * (ActionResponse or data object containing those)
     * This is preferred method to call from JS code.
     */
    public showFailedInModalWindow(result: any): Promise<ActionResponse> {
        if (isActionResponse(result)) {
            return this.showModalWindow(result, true);
        }
        if (result.data) {
            // TODO: check if necessary
            return this.showFailedInModalWindow(result.data);
        }
        this.showNonStandardError(result);
        throw result;
    }

    private showErrorToast(actionResponse: ActionResponse) {
        if (isActionResponseWithText(actionResponse) && !this.isIgnored(actionResponse.responseType)) {
            this.toastMessageService.showError(actionResponse.text);
        }
    }

    private showNonStandardError(error: any) {
        this.toastMessageService.showError(resolveErrorMessage(error));
    }

    private showModalWindow(actionResponse: ActionResponse, isFailed: boolean): Promise<ActionResponse> {
        if (!this.isIgnored(actionResponse.responseType)) {
            assertIsModalWindowType(actionResponse.responseType);
            return this.modalDialogService.openModalWindow(actionResponse.responseType, actionResponse as ModalWindowTypeToModelMap[typeof actionResponse.responseType]);
        }
        if (isFailed) {
            return Promise.reject(actionResponse);
        }
        return Promise.resolve(actionResponse);
    }

    private isIgnored(responseType: string): boolean {
        return (this.ignoredResponseTypes as readonly string[]).includes(responseType);
    }

    private isForceModal(responseType: string): boolean {
        return (this.forceModalResponseTypes as readonly string[]).includes(responseType);
    }
}
